// Read the flags object saved at login time.
// Returns { is_org_admin, is_org_user, is_platform_admin, role_slug, primary_org_plan }
export const getUserFlags = () => {
    try {
        return JSON.parse(sessionStorage.getItem('user_flags') || '{}');
    } catch {
        return {};
    }
};

// Returns true when the logged-in user is org_user (not org_admin).
// Use ONLY to hide actions that are exclusively org_admin — e.g. "Add Employee".
// Do NOT use for actions both roles share (Add Vendor, Create Risk, Report Exception, etc.).
export const isOrgUserOnly = () => {
    const flags = getUserFlags();
    return flags?.is_org_user === true && flags?.is_org_admin !== true;
};

export const formatText = (value = "") => {
    return value
      .toString()
      .trim()
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, char => char.toUpperCase());
  }

export const getPlanPermissions = (planInfo) => {
    // Tiers: 1: Free, 2: Essential, 3: Foundation, 4: Secure+
    const tier = planInfo?.plan_tier || 1;
    // Read plan_limits from backend when available; fall back to tier-based defaults
    const limits = planInfo?.plan_limits || {};

    const getLimit = (module, key, fallback) => {
        if (limits[module] && limits[module][key] !== undefined) return limits[module][key];
        return fallback;
    };

    return {
        isFreePlan: tier === 1,
        isEssentialPlan: tier === 2,
        isFoundationPlan: tier === 3,
        isSecurePlusPlan: tier === 4,

        // Module-level enabled flags — read directly from plan_limits
        modules: {
            external_exposure: getLimit('external_exposure', 'enabled', true),
            asset_inventory: getLimit('asset_inventory', 'enabled', true),
            risk_registry: getLimit('risk_registry', 'enabled', true),
            policy_center: getLimit('policy_center', 'enabled', true),
            tpcrm: getLimit('tpcrm', 'enabled', tier > 1),
            vulnerability_management: getLimit('vulnerability_management', 'enabled', tier > 1),
            grc: getLimit('grc', 'enabled', tier > 1),
            add_ons: getLimit('addon_services', 'enabled', tier > 1),
            // nova_ai lives under grc.nova_guidance in the real schema
            nova_ai: getLimit('grc', 'nova_guidance', tier === 4),
            quantum: getLimit('quantum_encryption_module', 'enabled', tier === 4),
        },

        features: {
            // Asset inventory
            public_assets: getLimit('asset_inventory', 'public_assets', true),
            cloud_integration: getLimit('asset_inventory', 'cloud_integration', tier > 1),
            // External exposure
            report_download: getLimit('external_exposure', 'report_download', tier > 1),
            domain_impersonation: getLimit('external_exposure', 'domain_impersonation', tier >= 3),
            weekly_cxo_email: getLimit('external_exposure', 'weekly_cxo_email', true),
            // TPCRM
            tpcrm_auto_schedule: getLimit('tpcrm', 'auto_schedule', tier >= 3),
            tpcrm_vendor_assessment_report: getLimit('tpcrm', 'vendor_assessment_report', tier >= 3),
            tpcrm_monthly_report: getLimit('tpcrm', 'monthly_report', tier > 1),
            // Risk Registry
            risk_registry_add_risk: getLimit('risk_registry', 'add_risk', true),
            risk_registry_download: getLimit('risk_registry', 'track_download_report', true),
            // Policy Center
            download_policy: getLimit('policy_center', 'download_policy', true),
            policy_customization: getLimit('policy_center', 'policy_customization', tier >= 3),
            // GRC
            grc_compliance_tracking: getLimit('grc', 'compliance_tracking', tier > 1),
            grc_share_questionnaires: getLimit('grc', 'share_questionnaires', tier > 1),
            grc_auto_mapping: getLimit('grc', 'auto_mapping', tier > 1),
            // VM
            vm_exploitability: getLimit('vulnerability_management', 'exploitability_detection', tier > 1),
            vm_patch_guidance: getLimit('vulnerability_management', 'patch_guidance', tier >= 3),
        },

        grc: {
            // '*' means all frameworks allowed
            frameworks: getLimit('grc', 'allowed_frameworks', tier === 4 ? ['*'] : (tier === 3 ? ['CIS', 'ISO 27001', 'SEBI', 'RBI', 'DPDPA', 'DORA', 'GDPR'] : (tier === 2 ? ['CIS', 'ISO 27001'] : []))),
        },

        limits: {
            max_vendors: getLimit('tpcrm', 'max_vendors', tier === 1 ? 0 : (tier === 2 ? 50 : (tier === 3 ? 50 : 100))),
            max_vm_licenses: getLimit('vulnerability_management', 'license_allocation', tier === 1 ? 0 : (tier === 2 ? 50 : (tier === 3 ? 250 : 500))),
        },

        addOns: {
            enabled: getLimit('addon_services', 'enabled', tier > 1),
            soc_24x7: getLimit('addon_services', 'soc_24x7', tier === 4),
            board_reporting: getLimit('addon_services', 'board_reporting', tier >= 3),
            dpo_hours: getLimit('addon_services', 'dpo_hours_per_month', tier === 4 ? 8 : (tier === 3 ? 2 : 0)),
            sast_per_year: getLimit('addon_services', 'sast_per_year', tier === 4 ? 10 : (tier === 3 ? 4 : 0)),
            ciso_hours: getLimit('addon_services', 'ciso_hours_per_month', tier === 4 ? 730 : (tier === 3 ? 8 : (tier === 2 ? 2 : 0))),
            phishing_simulation: getLimit('addon_services', 'phishing_simulation', tier === 4 ? 'quarterly_all' : (tier >= 2 ? 'annual_100' : 'none')),
            vapt: getLimit('addon_services', 'vapt', 'none'),
            security_awareness_training: getLimit('addon_services', 'security_awareness_training', tier === 4 ? 'virtual_onsite' : (tier === 3 ? 'virtual' : 'none')),
            app_architecture_review_count: getLimit('addon_services', 'app_architecture_review_count', tier === 4 ? 5 : (tier === 3 ? 1 : 0)),
        }
    };
};